import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { enqueueSnackbar } from "notistack"
import { Grid, Stack, Typography } from "@mui/material"

import { ProductGroups, ProductsTableItem } from "../../../../../../modules/merchant/admin/products/productsTypes"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { MerchantProductsQuery } from "../../../../../../modules/merchant/admin/products/productsApi"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { deleteProductFavorite, saveProductFavorite } from "../../../createOrderApi"
import { SelectedPriceListItem } from "../../../createOrderTypes"
import { DeliveryTimesField, DynamicConfigKey } from "../../form/formTypes"

import { CreditNoteOverlay } from "./CreditNoteOverlay"
import { Product } from "./Product"
import {
    buildPriceMap,
    collectSelectedLines,
    getProductQuantityFromDeliveries,
    hasPriceDiff,
    updatePricesInDeliveries,
} from "./helpers"
import { PriceMismatchDialog } from "./PriceMismatchDialog"

const useStyles = makeStyles()((theme) => ({
    container: {
        flexGrow: "1",
        padding: "1.5rem",
        background: theme.palette.tailwind.slate[100],
        minHeight: 0,
        overflowY: "auto",
    },
    emptyWrap: {
        textAlign: "center",
        padding: "2rem 1rem",
        fontStyle: "italic",
        color: theme.palette.tailwind.slate[400],
    },
    disabledContent: {
        pointerEvents: "none",
        position: "relative",
        overflowY: "initial",
    },
}))

export type ShopFiltersTypes = Readonly<{
    isFavorite: boolean
    search: string
}>

type ProductContainerProps = Readonly<{
    list?: ProductGroups[]
    searchRegex: RegExp | undefined
    isListView: boolean
    isFavoriteFilter: boolean
    handleCloseMainDialog: () => void
    isRefundOrder: boolean
    orderId?: number
}>

export const ProductContainer = memo(
    ({
        list,
        searchRegex,
        isListView,
        isFavoriteFilter,
        handleCloseMainDialog,
        isRefundOrder = false,
        orderId,
    }: ProductContainerProps) => {
        const { classes, cx } = useStyles()
        const { l10n } = useAppLocalization()

        const [{ value: deliveries }, , deliveriesHelpers] = useField<DeliveryTimesField>(
            DynamicConfigKey.MULTIPLE_DELIVERIES,
        )
        const [{ value: priceListItem }] = useField<SelectedPriceListItem | null>("priceList")

        const [showPriceWarning, setShowPriceWarning] = useState(false)
        const warnedRef = useRef(false)

        const priceListIncludeVat = !!priceListItem?.priceListIncludeVat
        const priceCompareOptions = useMemo(
            () => ({ isRefundOrder, priceListIncludeVat }),
            [isRefundOrder, priceListIncludeVat],
        )

        const priceById = useMemo(() => buildPriceMap(list, priceCompareOptions), [list, priceCompareOptions])
        const selectedLines = useMemo(() => collectSelectedLines(deliveries), [deliveries])

        const diff = useMemo(() => hasPriceDiff(priceById, selectedLines), [priceById, selectedLines])

        useEffect(() => {
            if (warnedRef.current) return
            if (!list || list.length === 0) return
            if (selectedLines.length === 0) return
            if (!diff) return

            warnedRef.current = true
            setShowPriceWarning(true)
        }, [list, selectedLines.length, diff])

        const { mutate: callSaveProductFavorite } = useApiMutation(saveProductFavorite, {
            async onSuccess(_, _data, context) {
                await context.queryClient.invalidateQueries({ queryKey: [MerchantProductsQuery.LIST] })
            },
        })

        const { mutate: callDeleteProductFavorite } = useApiMutation(deleteProductFavorite, {
            async onSuccess(_, _data, context) {
                await context.queryClient.invalidateQueries({ queryKey: [MerchantProductsQuery.LIST] })
            },
        })

        const addToBasket = useCallback(
            (product: ProductsTableItem, quantity: number) => {
                const activeTime = deliveries.activeTime

                if (!activeTime) {
                    enqueueSnackbar(l10n.getString("create-order-delivery-times-empty-error"), { variant: "error" })
                    return
                }
                const updatedDeliveries = {
                    ...deliveries,
                    deliveryTimes: deliveries.deliveryTimes.map((delivery) => {
                        if (delivery.time === activeTime) {
                            const existingIndex = delivery.orderLines.findIndex(
                                (item) => item.id === product.id && !item.isExtraService,
                            )
                            let newOrderLines = [...delivery.orderLines]

                            if (quantity > 0) {
                                if (existingIndex !== -1) {
                                    newOrderLines[existingIndex] = { ...newOrderLines[existingIndex], quantity }
                                } else {
                                    newOrderLines = [...newOrderLines, { ...product, quantity }]
                                }
                            } else {
                                newOrderLines = newOrderLines.filter(
                                    (item) => !(item.id === product.id && !item.isExtraService),
                                )
                            }

                            return { ...delivery, orderLines: newOrderLines }
                        }
                        return delivery
                    }),
                }

                deliveriesHelpers.setValue(updatedDeliveries)
            },
            [deliveries, deliveriesHelpers],
        )

        const handleFavoriteProduct = useCallback(
            (productId: number, isFavorite: boolean): Promise<boolean> =>
                new Promise((resolve) => {
                    const onSuccess = () => resolve(true)
                    const onError = () => resolve(false)

                    if (isFavorite) {
                        callSaveProductFavorite(
                            { id: productId, productType: "MEETING_SERVICE" },
                            { onSuccess, onError },
                        )
                    } else {
                        callDeleteProductFavorite({ productId }, { onSuccess, onError })
                    }
                }),
            [callSaveProductFavorite, callDeleteProductFavorite],
        )

        const getProductQuantity = useCallback(
            (productId: number) => getProductQuantityFromDeliveries(deliveries, productId),
            [deliveries],
        )

        const handleCloseDialog = () => {
            setShowPriceWarning(false)
        }

        const handleChangePrices = useCallback(() => {
            if (!deliveries || !Array.isArray(deliveries.deliveryTimes)) {
                handleCloseDialog()
                return
            }

            const { updated, changed } = updatePricesInDeliveries(deliveries, priceById)
            if (changed) {
                deliveriesHelpers.setValue(updated)
            }
            handleCloseDialog()
        }, [deliveries, deliveriesHelpers, priceById])

        return (
            <Stack gap="2rem" className={cx(classes.container, { [classes.disabledContent]: isRefundOrder })}>
                {list && list.length > 0 ? (
                    list.map((group) => {
                        return (
                            <Stack gap="1.5rem" key={group.id}>
                                <Typography variant="h6">{group?.name}</Typography>
                                <Grid container gap="1.5rem" alignItems="flex-start">
                                    {group?.products.map((product) => (
                                        <Product
                                            key={product.id}
                                            product={product}
                                            handleFavoriteProduct={handleFavoriteProduct}
                                            addToBasket={addToBasket}
                                            quantity={getProductQuantity(product.id)}
                                            searchRegex={searchRegex}
                                            isListView={isListView}
                                        />
                                    ))}
                                </Grid>
                            </Stack>
                        )
                    })
                ) : (
                    <Stack gap=".75rem" className={classes.emptyWrap}>
                        {isFavoriteFilter ? (
                            <>
                                <LocalizedStrict id="create-order-products-empty-favorites-title">
                                    <Typography variant="h6">You do not have any favorite items</Typography>
                                </LocalizedStrict>
                                <LocalizedStrict id="create-order-products-empty-favorites-description">
                                    <Typography>
                                        You can add favorite items so that you can complete an order more quickly. Add a
                                        favorite item by clicking on the heart icon found on the product card
                                    </Typography>
                                </LocalizedStrict>
                            </>
                        ) : (
                            <LocalizedStrict id="create-order-products-empty">
                                <Typography variant="h6">No results</Typography>
                            </LocalizedStrict>
                        )}
                    </Stack>
                )}
                {showPriceWarning && (
                    <PriceMismatchDialog
                        open={showPriceWarning}
                        handleCloseDialog={handleCloseDialog}
                        handleCloseMainDialog={handleCloseMainDialog}
                        handleChangePrices={handleChangePrices}
                        isRefundOrder={isRefundOrder}
                    />
                )}
                {isRefundOrder && <CreditNoteOverlay orderId={orderId} />}
            </Stack>
        )
    },
)
